// PublicFormPage — landing rendered at /form/:slug. NO auth required.
//
// Fetches the form definition from /forms/:slug and renders it dynamically
// using the same PublicFormRenderer used by the editor preview, so what the
// agent sees in preview is byte-equal to what visitors see.
//
// On submit:
//   - POSTs to /forms/:slug/submit
//   - Backend creates a Lead row + LeadFormSubmission + (best-effort) emails
//     the form's owner agent.
//   - We show the thank-you screen, OR redirect if redirectUrl is set.

import React, { useEffect, useState } from "react";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import { getPublicForm, submitPublicForm } from "../api.js";
import { useCaptcha } from "./Captcha.jsx";
import { PublicFormRenderer } from "./LeadFormsView.jsx";

function isSafeRedirectUrl(raw) {
  if (typeof raw !== "string" || !raw) return false;
  let u;
  try {
    u = new URL(raw, window.location.origin);
  } catch {
    return false;
  }
  return u.protocol === "http:" || u.protocol === "https:";
}

export function PublicFormPage({ slug, lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(null); // null | { thankYouTitle, thankYouMessage }
  // Captcha: apagado (sin nada en pantalla) hasta que el servidor tenga claves.
  const captcha = useCaptcha(lang);

  useEffect(() => {
    let cancelled = false;
    setLoadError("");
    getPublicForm(slug)
      .then((f) => { if (!cancelled) setForm(f); })
      .catch((err) => { if (!cancelled) setLoadError(err.message || String(err)); });
    return () => { cancelled = true; };
  }, [slug]);

  async function handleSubmit(values) {
    if (captcha.activo && !captcha.token) {
      setSubmitError(t("Confirma que no eres un robot (el recuadro de arriba del botón).", "Please confirm you're not a robot (the box above the button)."));
      return;
    }
    setBusy(true);
    setSubmitError("");
    try {
      const r = await submitPublicForm(slug, values, captcha.token);
      if (r.redirectUrl) {
        // Audit H2: only navigate to http/https URLs. Anything else
        // (javascript:, data:, vbscript:…) would execute in the visitor's
        // browser. Backend now validates on save too, but defense in
        // depth — old rows may still have a hostile value.
        if (isSafeRedirectUrl(r.redirectUrl)) {
          window.location.href = r.redirectUrl;
          return;
        }
        // Bad URL → fall through to the normal thank-you screen instead
        // of doing nothing silently.
      }
      setDone({
        title: r.thankYouTitle || form?.thankYouTitle || t("¡Gracias!", "Thanks!"),
        message: r.thankYouMessage || form?.thankYouMessage
          || t("Recibimos tu información. Te contactaremos pronto.",
                "We got your info. We'll be in touch soon."),
      });
    } catch (err) {
      setSubmitError(err.message || String(err));
      // El token vale una vez: se pide otro para el siguiente intento.
      captcha.reiniciar();
    } finally {
      setBusy(false);
    }
  }

  // ---- Layout shell --------------------------------------------------
  const brand = form?.brandColor || "#00d4ff";
  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(180deg, ${brand}11 0%, #f4f7fb 220px)`,
      padding: "2rem 1rem",
      fontFamily: "system-ui,-apple-system,Segoe UI,sans-serif",
      color: "#0a1628",
    }}>
      <div style={{ maxWidth: 580, margin: "0 auto" }}>

        {/* Brand header */}
        <div style={{ textAlign: "center", marginBottom: "1rem" }}>
          {/* El logo de la agencia encima del nombre (Gabriel De Silva,
              2026-10-05). Si la agencia no tiene logo, queda solo el nombre. */}
          {form?.agencyLogoUrl && (
            <img src={form.agencyLogoUrl} alt={form.agencyName || ""}
                 style={{ display: "block", margin: "0 auto .5rem", maxHeight: 64, maxWidth: 220, objectFit: "contain" }} />
          )}
          {form?.agencyName ? (
            <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "#0a1628", letterSpacing: "-.01em" }}>
              {form.agencyName}
            </div>
          ) : (
            <div style={{ fontSize: ".85rem", color: "#5b7a9a", letterSpacing: ".05em" }}>
              powered by <strong>Deskte</strong>
            </div>
          )}
        </div>

        {loadError && (
          <Card>
            <ErrorBlock title={t("No pudimos cargar este formulario", "Couldn't load this form")} body={loadError} />
          </Card>
        )}

        {!form && !loadError && (
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#5b7a9a" }}>
              <Loader2 size={16} className="dkt-spin" />
              {t("Cargando…", "Loading…")}
            </div>
          </Card>
        )}

        {form && done && (
          <Card>
            <div style={{ textAlign: "center", padding: "1rem 0" }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16, margin: "0 auto 1rem",
                background: "#10b98122", border: "1px solid #10b98166",
                display: "grid", placeItems: "center",
              }}>
                <Check size={28} color="#10b981" />
              </div>
              <h2 style={{ margin: "0 0 .75rem", color: "#0a1628" }}>{done.title}</h2>
              <p style={{ color: "#5b7a9a", lineHeight: 1.55, margin: 0 }}>{done.message}</p>
            </div>
          </Card>
        )}

        {form && !done && (
          <>
            {submitError && (
              <div style={{
                background: "#fee2e2", border: "1px solid #ef4444", borderRadius: 10,
                padding: ".75rem 1rem", marginBottom: 12, color: "#991b1b",
                display: "flex", alignItems: "center", gap: 10, fontSize: ".88rem",
              }}>
                <AlertCircle size={16} /><span>{submitError}</span>
              </div>
            )}
            {captcha.widget}
            <PublicFormRenderer form={form} onSubmit={handleSubmit} busy={busy} lang={lang} />
            {form.agentName && (
              <div style={{ textAlign: "center", marginTop: 14, fontSize: ".78rem", color: "#5b7a9a" }}>
                {t("Atendido por", "Handled by")} <strong style={{ color: "#3a5a7a" }}>{form.agentName}</strong>
              </div>
            )}
          </>
        )}

        <div style={{ textAlign: "center", marginTop: "2rem", fontSize: ".7rem", color: "#7d97b3" }}>
          {t("Tus datos están protegidos. Solo el agente puede verlos.",
             "Your data is protected. Only the agent can see it.")} · powered by <strong>Deskte</strong>
        </div>
      </div>
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 14, padding: "1.5rem",
      boxShadow: "0 8px 24px rgba(0,0,0,.06)",
    }}>{children}</div>
  );
}

function ErrorBlock({ title, body }) {
  return (
    <div style={{ textAlign: "center", padding: "1rem 0" }}>
      <div style={{
        width: 56, height: 56, borderRadius: 14, margin: "0 auto 1rem",
        background: "#fee2e2", border: "1px solid #ef4444",
        display: "grid", placeItems: "center",
      }}>
        <AlertCircle size={24} color="#ef4444" />
      </div>
      <h3 style={{ margin: "0 0 .5rem", color: "#0a1628" }}>{title}</h3>
      <p style={{ color: "#5b7a9a", lineHeight: 1.55, margin: 0, fontSize: ".88rem" }}>{body}</p>
    </div>
  );
}
